import { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Save } from 'lucide-react';
import { getSiteSettings, updateSiteSettings } from '../../lib/firebaseService';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

export default function AdminAbout() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    about_title: '',
    about_content: '',
    mission_title: '',
    mission_content: '',
    vision_title: '',
    vision_content: '',
    contact_email: '',
    contact_phone: '',
    contact_address: ''
  });

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard');
      return;
    }
    loadSettings();
  }, [user, navigate]);

  const loadSettings = async () => {
    try {
      const settings = await getSiteSettings();
      if (settings) {
        setFormData({
          about_title: settings.about_title || 'Haqqımızda',
          about_content: settings.about_content || '',
          mission_title: settings.mission_title || 'Missiyamız',
          mission_content: settings.mission_content || '',
          vision_title: settings.vision_title || 'Vizyonumuz',
          vision_content: settings.vision_content || '',
          contact_email: settings.contact_email || '',
          contact_phone: settings.contact_phone || '',
          contact_address: settings.contact_address || ''
        });
      }
    } catch (error) {
      console.error('Ayarlar yüklənə bilmədi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateSiteSettings(formData);
      toast.success('Məlumatlar saxlanıldı');
    } catch (error) {
      console.error('Xəta:', error);
      toast.error('Əməliyyat uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Yüklənir...</p>
      </div>
    );
  }

  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link'],
      ['clean']
    ]
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-about-page">
      <header className="bg-white border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>
            </Link>
            <h1 className="text-2xl font-bold">Haqqımızda Səhifəsi</h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <form onSubmit={handleSubmit} className="max-w-3xl space-y-8">
          {/* About Section */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-4">Haqqımızda Bölümü</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="about_title">Başlıq</Label>
                <Input
                  id="about_title"
                  value={formData.about_title}
                  onChange={(e) => setFormData({ ...formData, about_title: e.target.value })}
                  placeholder="Haqqımızda"
                />
              </div>
              <div>
                <Label htmlFor="about_content">Mətn</Label>
                <div className="mt-2">
                  <ReactQuill
                    theme="snow"
                    value={formData.about_content}
                    onChange={(value) => setFormData({ ...formData, about_content: value })}
                    modules={modules}
                    placeholder="Şirkətiniz haqqında məlumat yazın..."
                    style={{ height: '200px', marginBottom: '50px' }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-4">Missiya</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="mission_title">Başlıq</Label>
                <Input
                  id="mission_title"
                  value={formData.mission_title}
                  onChange={(e) => setFormData({ ...formData, mission_title: e.target.value })}
                  placeholder="Missiyamız"
                />
              </div>
              <div>
                <Label htmlFor="mission_content">Mətn</Label>
                <Textarea
                  id="mission_content"
                  value={formData.mission_content}
                  onChange={(e) => setFormData({ ...formData, mission_content: e.target.value })}
                  rows={4}
                  placeholder="Missiya mətnini yazın..."
                />
              </div>
            </div>
          </div>

          {/* Vision Section */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-4">Vizyon</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="vision_title">Başlıq</Label>
                <Input
                  id="vision_title"
                  value={formData.vision_title}
                  onChange={(e) => setFormData({ ...formData, vision_title: e.target.value })}
                  placeholder="Vizyonumuz"
                />
              </div>
              <div>
                <Label htmlFor="vision_content">Mətn</Label>
                <Textarea
                  id="vision_content"
                  value={formData.vision_content}
                  onChange={(e) => setFormData({ ...formData, vision_content: e.target.value })}
                  rows={4}
                  placeholder="Vizyon mətnini yazın..."
                />
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-white rounded-xl border border-border p-6">
            <h2 className="text-xl font-semibold mb-4">Əlaqə Məlumatları</h2>
            <div className="space-y-4">
              <div>
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                  placeholder="info@example.com"
                />
              </div>
              <div>
                <Label htmlFor="contact_phone">Telefon</Label>
                <Input
                  id="contact_phone"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                  placeholder="+994 XX XXX XX XX"
                />
              </div>
              <div>
                <Label htmlFor="contact_address">Ünvan</Label>
                <Textarea
                  id="contact_address"
                  value={formData.contact_address}
                  onChange={(e) => setFormData({ ...formData, contact_address: e.target.value })}
                  rows={2}
                  placeholder="Şirkət ünvanı..."
                />
              </div>
            </div>
          </div>

          <Button type="submit" size="lg" className="w-full" disabled={saving}>
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saxlanılır...' : 'Yadda Saxla'}
          </Button>
        </form>
      </main>
    </div>
  );
}
